/**
 * @fileoverview RateCardFormDialog — create or edit a multiplier rate card
 * (MOR-1038).
 *
 * The preview table is the point of the dialog: a multiplier and a rounding
 * step are abstract, the resulting dollar rate per grade is what the firm is
 * actually agreeing to. It is computed with the same `applyCard` the server
 * uses, against the firm's current standard rates.
 *
 * Editing a card that jobs are already on re-values their unapproved time, so
 * the dialog says how many jobs that is before saving.
 */

import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { applyCard } from "@cvx/timesheets/rateMath";

import { extractErrorMessage } from "@/utils/errors";
import { Button, Label } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  describeMultiplier,
  describeRounding,
  formatRate,
} from "./rateCards.helpers";

export interface EditableRateCard {
  _id: Id<"rateCards">;
  name: string;
  multiplier: number;
  roundTo?: number;
  jobCount: number;
}

interface FormState {
  name: string;
  multiplier: string;
  roundTo: string;
}

/** Beacon's stated default: round to the nearest $10 after multiplying. */
const NEW_CARD: FormState = { name: "", multiplier: "", roundTo: "10" };

export function RateCardFormDialog({
  open,
  orgId,
  card,
  grades,
  onOpenChange,
}: {
  open: boolean;
  orgId: Id<"organizations">;
  /** The card being edited, or `null` to create one. */
  card: EditableRateCard | null;
  /** The firm's grades with their current standard rate, for the preview. */
  grades: { _id: Id<"staffGrades">; name: string; currentRate?: number }[];
  onOpenChange: (open: boolean) => void;
}) {
  const createCard = useMutation(api.timesheets.rateCards.create);
  const updateCard = useMutation(api.timesheets.rateCards.update);
  const [form, setForm] = useState<FormState>(NEW_CARD);
  const [saving, setSaving] = useState(false);

  // Reload the form whenever the dialog opens, for whichever card it is for.
  useEffect(() => {
    if (!open) return;
    setForm(
      card
        ? {
            name: card.name,
            multiplier: String(card.multiplier),
            roundTo: card.roundTo !== undefined ? String(card.roundTo) : "",
          }
        : NEW_CARD,
    );
  }, [open, card]);

  const multiplier = Number(form.multiplier);
  const roundTo = form.roundTo.trim() === "" ? undefined : Number(form.roundTo);
  const multiplierValid =
    form.multiplier.trim() !== "" &&
    Number.isFinite(multiplier) &&
    multiplier >= 0.01 &&
    multiplier <= 10;
  const roundToValid =
    roundTo === undefined || (Number.isFinite(roundTo) && roundTo > 0);
  const canSave = !!form.name.trim() && multiplierValid && roundToValid;
  const ratedGrades = grades.filter(
    (g): g is typeof g & { currentRate: number } => g.currentRate !== undefined,
  );

  const handleSave = async () => {
    if (!canSave || saving) return;
    setSaving(true);
    try {
      if (card) {
        await updateCard({
          rateCardId: card._id,
          name: form.name,
          multiplier,
          roundTo: roundTo ?? null,
        });
        toast.success("Rate card updated.");
      } else {
        await createCard({ orgId, name: form.name, multiplier, roundTo });
        toast.success("Rate card added.");
      }
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not save the rate card."));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{card ? "Edit rate card" : "New rate card"}</DialogTitle>
          <DialogDescription>
            A rate card charges your standard rates at a multiple. Put a job on
            it from the job&apos;s Timesheets tab.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="rate-card-name">Name</Label>
            <Input
              id="rate-card-name"
              value={form.name}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, name: e.target.value }))
              }
              placeholder="e.g. Bank and receivership"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="rate-card-multiplier">Multiplier</Label>
              <Input
                id="rate-card-multiplier"
                type="number"
                min={0.01}
                max={10}
                step={0.05}
                value={form.multiplier}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, multiplier: e.target.value }))
                }
                placeholder="e.g. 0.75"
                className="tabular-nums"
              />
              <p className="text-xs text-muted-foreground">
                {multiplierValid
                  ? describeMultiplier(multiplier)
                  : "0.75 is a 25% discount, 1.25 a 25% uplift."}
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="rate-card-round-to">Round to nearest ($)</Label>
              <Input
                id="rate-card-round-to"
                type="number"
                min={0}
                step={1}
                value={form.roundTo}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, roundTo: e.target.value }))
                }
                placeholder="No rounding"
                className="tabular-nums"
              />
              <p className="text-xs text-muted-foreground">
                {roundToValid
                  ? `Rounds to the ${describeRounding(roundTo)}.`
                  : "Must be greater than zero, or blank."}
              </p>
            </div>
          </div>

          <div className="rounded-md border border-border/60">
            <div className="grid grid-cols-3 gap-2 border-b border-border/60 bg-muted/40 px-3 py-1.5 text-xs font-medium text-muted-foreground">
              <span>Grade</span>
              <span className="text-right">Standard</span>
              <span className="text-right">On this card</span>
            </div>
            {ratedGrades.length === 0 ? (
              <p className="px-3 py-3 text-xs text-muted-foreground">
                No grades have a current rate yet, so there is nothing to
                preview.
              </p>
            ) : (
              ratedGrades.map((g) => (
                <div
                  key={g._id}
                  className="grid grid-cols-3 gap-2 px-3 py-1.5 text-sm tabular-nums"
                >
                  <span className="truncate">{g.name}</span>
                  <span className="text-right text-muted-foreground">
                    {formatRate(g.currentRate)}
                  </span>
                  <span className="text-right font-medium">
                    {multiplierValid && roundToValid
                      ? formatRate(
                          applyCard(g.currentRate, { multiplier, roundTo }),
                        )
                      : "—"}
                  </span>
                </div>
              ))
            )}
          </div>

          {card !== null && card.jobCount > 0 && (
            <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              {card.jobCount === 1
                ? "1 job is on this card."
                : `${card.jobCount} jobs are on this card.`}{" "}
              Changing its terms re-values their unapproved time. Approved time
              keeps the rate it was approved at.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!canSave || saving}
            onClick={() => void handleSave()}
          >
            {saving && <Loader2 className="size-4 animate-spin" />}
            {card ? "Save changes" : "Add rate card"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
